import * as React from "react";

import { api } from "@repo/backend";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/card";
import { Skeleton } from "@repo/ui/components/skeleton";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { CameraIcon } from "lucide-react";

import { sessionImages, type SessionType } from "@/lib/scheduling";

export const Route = createFileRoute("/scheduling/")({
  head: () => ({
    meta: [
      { title: "Schedule Your Photo Shot | EWC26 Staff" },
      {
        name: "description",
        content:
          "Schedule your own photo shot at EWC26 Staff. Pick a Fullbody Light, Portrait Dark, Editorial Solo or Editorial Team session and book a time that works for you.",
      },
    ],
  }),
  component: SessionTypePage,
});

function SessionTypePage() {
  const options = useQuery(api.scheduling.getSessionOptions);
  return (
    <section className="min-h-0 overflow-auto bg-[#191919] p-5 text-white sm:p-8">
      <div className="w-full">
        <div className="text-center">
          <h1 className="px-12 text-lg font-semibold italic">Choose your session</h1>
          <p className="mt-2 text-sm text-white/55">Select one option to see available times.</p>
        </div>
        {options === undefined ? (
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="rounded-xl border border-white/10 p-4">
                <Skeleton className="h-5 w-32 bg-white/10" />
                <Skeleton className="mt-5 h-4 w-20 bg-white/10" />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {options.map((option) => {
              const type = option.type as SessionType;
              return (
                <Link key={type} to="/scheduling/calendar" search={{ type }} className="group">
                  <Card className="h-full overflow-hidden border-white/10 bg-[#191919] py-0 transition-colors group-hover:bg-white/[0.06]">
                    <div className="flex">
                      <SessionImage type={type} />
                      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 py-4 pr-4">
                        <CardHeader className="p-0">
                          <CardTitle className="text-lg">{option.label}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0 text-sm text-white/55">
                          {option.durationMinutes} minutes
                        </CardContent>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function SessionImage({ type }: { type: SessionType }) {
  const [failed, setFailed] = React.useState(false);
  if (failed) {
    return (
      <div className="flex w-36 shrink-0 items-center justify-center self-stretch p-2">
        <div className="flex w-full items-center justify-center rounded-lg bg-white/[0.03]">
          <CameraIcon className="size-8 text-white/25" />
        </div>
      </div>
    );
  }
  return (
    <div className="w-36 shrink-0 self-stretch p-2">
      <img
        src={sessionImages[type]}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="h-full w-full rounded-lg object-cover"
      />
    </div>
  );
}
